import React, { useState, useEffect, useCallback } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import moment from "moment";
import { getSignedUrl } from "@/services/media";

export default function SignedLinkWrapper({ children, privateUrl }) {
  const [signedUrl, setSignedUrl] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);

  // Fetch the signed URL and update state
  const fetchSignedUrl = useCallback(async () => {
    if (!privateUrl) return;

    const controller = new AbortController();
    const signal = controller.signal; // Get the abort signal

    try {
      const filename = new URL(privateUrl).pathname.replace("/", "");
      const url = await getSignedUrl(filename, { signal }); // Pass the signal to the fetch request
      setSignedUrl(url);

      // Parse expiration timestamp from the signed URL
      const urlObj = new URL(url);
      const expires = Number(urlObj.searchParams.get("Expires"));
      if (!isNaN(expires)) {
        setExpirationDate(moment(expires * 1000));
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("Error fetching signed URL:", error);
        setSignedUrl(null); // Reset signed URL on error
      }
    }

    // Cleanup function to abort the fetch request
    return () => {
      controller.abort();
    };
  }, [privateUrl]);

  // Initial fetch and re-fetch if expired
  useEffect(() => {
    const cleanupFetch = fetchSignedUrl();
    return () => {
      typeof cleanupFetch === "function" && cleanupFetch();
    };
  }, [fetchSignedUrl]);

  // Re-fetch if URL is expired
  useEffect(() => {
    const now = moment();
    if (expirationDate && expirationDate.isBefore(now)) {
      fetchSignedUrl();
    }
  }, [expirationDate, fetchSignedUrl]);

  if (!signedUrl) {
    return <LoadingOutlined />;
  }

  // Pass signedUrl to children as a function
  return <>{children(signedUrl)}</>;
}
